import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Mail, CalendarCheck, MapPin as MapPinIcon, ClipboardCheck, Inbox,
  Phone, FileCheck2, Send, CheckCircle2,
  MapPin, ShieldCheck, BadgePoundSterling, Users,
  ArrowRight, ArrowDownRight,
} from 'lucide-react';
import CtaBanner from '../components/CtaBanner';
import PageMeta from '../components/PageMeta';
import { services } from '../mock/mock';
import { buildBreadcrumbLd } from '../lib/structuredData';

const story = [
  {
    icon: Mail,
    title: 'You raise an order',
    short: 'Raise an order',
    text:
      'Log in to our system and raise the job — drop in the address, the service you need and any access notes. Order logged, job number issued.',
    side: 'You',
    accent: 'var(--rs-green2)',
    image: services[1].image,
  },
  {
    icon: CalendarCheck,
    title: 'We confirm and schedule',
    short: 'We confirm',
    text:
      'A vetted local specialist is matched to your job and we send you written confirmation with the attendance window. No back-and-forth, no chasing your diary.',
    side: 'Us',
    accent: 'var(--rs-coral)',
    image: services[3].image,
  },
  {
    icon: MapPinIcon,
    title: 'Our clerk attends',
    short: 'Clerk attends',
    text:
      'Our specialist arrives at the property, photographs every room and completes the work to the brief. Once on-site work is done, the draft is queued for review.',
    side: 'Us',
    accent: 'var(--rs-coral)',
    image: services[2].image,
  },
  {
    icon: ClipboardCheck,
    title: 'We review before release',
    short: 'Reviewed',
    text:
      'Before anything reaches you, the clerk\u2019s draft is reviewed \u2014 photos, descriptions and formatting checked against our standards. Anything that needs tightening is flagged and put right.',
    side: 'Us',
    accent: 'var(--rs-coral)',
    image: services[1].image,
  },
  {
    icon: Inbox,
    title: 'Report lands in your inbox',
    short: 'Sent to you',
    text:
      'Once reviewed and approved, the polished report appears in your dashboard. Your branch forwards to the tenant or landlord \u2014 job done.',
    side: 'You',
    accent: 'var(--rs-green2)',
    image: services[0].image,
  },
];

const onboarding = [
  { n: '01', icon: Phone, title: 'You send us a quick intro', text: 'A short email or call telling us about your branch, the services you need and your typical postcodes.' },
  { n: '02', icon: FileCheck2, title: 'We send back a setup pack', text: 'A simple one-pager with your dedicated point of contact, an order template and your agency-specific pricing.' },
  { n: '03', icon: Send, title: 'You raise your first order', text: "Reply with the address and we'll take it from there. We'll have you live and ordering in no time." },
];

const trust = [
  { icon: ShieldCheck, t: '£2m PI & PL insured' },
  { icon: Users, t: 'Referenced, DBS-checked clerks' },
  { icon: MapPin, t: 'Nationwide UK coverage' },
  { icon: BadgePoundSterling, t: 'One invoice, every branch' },
];

const HowItWorks = () => {
  return (
    <div>
      <PageMeta
        title="How it works · Residential Support — Five simple steps"
        description="From raising an order to receiving a finished, dispute-ready report — here is exactly how Residential Support handles UK property jobs for your branch."
        path="/how-it-works"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(
            buildBreadcrumbLd([
              { label: 'Home', path: '/' },
              { label: 'How it works', path: '/how-it-works' },
            ])
          )}
        </script>
      </Helmet>
      {/* ── Compact text hero + process diagram ─────────────────── */}
      <section className="bg-[var(--rs-cream)] relative overflow-hidden">
        {/* soft backdrop shapes */}
        <div className="absolute -top-24 -left-24 w-[360px] h-[360px] rounded-full bg-[var(--rs-green2)]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-[360px] h-[360px] rounded-full bg-[var(--rs-coral)]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 pt-16 pb-12 lg:pt-24 lg:pb-16 text-center">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--rs-green)] mb-4">How it works</div>
          <h1 className="h-display text-5xl md:text-6xl lg:text-7xl text-[var(--rs-ink)] leading-[1.02]">
            Five simple steps. <em className="text-[var(--rs-coral)] not-italic">One trusted partner.</em>
          </h1>
          <p className="mt-6 text-[var(--rs-ink)]/70 text-lg max-w-2xl mx-auto">
            Here is exactly what happens between you raising an order and the finished report landing in your inbox.
          </p>
        </div>

        {/* Process diagram band */}
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 pb-16 lg:pb-24">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-2 relative">
            {/* connector line (desktop only) */}
            <div className="hidden md:block absolute top-7 left-[10%] right-[10%] h-px border-t-2 border-dashed border-[#D9D1F0]" aria-hidden />

            {story.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="relative flex flex-col items-center text-center">
                  <div
                    className="w-14 h-14 rounded-full grid place-items-center text-white shadow-lg ring-8 ring-[var(--rs-cream)]"
                    style={{ background: s.accent }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] mt-4" style={{ color: s.accent }}>
                    Step {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="h-display text-base md:text-[15px] text-[var(--rs-ink)] mt-1 px-1 whitespace-nowrap">{s.short}</div>
                  <div className="text-xs text-[var(--rs-ink)]/55 mt-1">{s.side}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Vertical timeline (with central rail) ─────────────────── */}
      <section className="sect bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">A day in the life of a job</div>
            <h2 className="h-display text-3xl md:text-4xl text-[var(--rs-ink)] leading-snug">
              The same predictable rhythm, every time.
            </h2>
          </div>

          <div className="relative mt-14">
            {/* vertical rail */}
            <div className="absolute top-2 bottom-2 left-7 md:left-9 w-px bg-gradient-to-b from-[var(--rs-green2)] via-[#D9D1F0] to-[var(--rs-coral)]" aria-hidden />

            <ol className="space-y-10 md:space-y-12">
              {story.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={i} className="relative pl-20 md:pl-24">
                    {/* node */}
                    <div className="absolute left-0 top-0 flex items-center">
                      <div
                        className="w-14 h-14 md:w-[72px] md:h-[72px] rounded-full grid place-items-center text-white ring-8 ring-white shadow-md shrink-0"
                        style={{ background: s.accent }}
                      >
                        <Icon className="w-5 h-5 md:w-6 md:h-6" />
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-start gap-5">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: s.accent }}>
                            Step {String(i + 1).padStart(2, '0')}
                          </span>
                          <span className="text-[var(--rs-ink)]/35">·</span>
                          <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--rs-ink)]/55">{s.side}</span>
                        </div>
                        <h3 className="h-display text-2xl md:text-3xl text-[var(--rs-ink)] leading-tight">{s.title}</h3>
                        <p className="mt-2.5 text-[var(--rs-ink)]/70 leading-relaxed">{s.text}</p>
                      </div>
                      <img
                        src={s.image}
                        alt=""
                        className="w-full md:w-44 lg:w-52 h-32 md:h-32 lg:h-36 object-cover rounded-xl shadow-sm shrink-0"
                      />
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="mt-12 text-center text-sm text-[var(--rs-ink)]/55 italic">
            Every job is tracked end-to-end — timelines vary by service, access and postcode.
          </div>
        </div>
      </section>

      {/* ── Onboarding (no image) ─────────────────────────────────── */}
      <section className="sect bg-[var(--rs-cream)]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">Getting started</div>
              <h2 className="h-display text-4xl md:text-5xl text-[var(--rs-ink)]">Set up in three short steps.</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-0 md:gap-0 rounded-[22px] overflow-hidden border border-[#E4DFF7] bg-white">
            {onboarding.map((o, i) => (
              <div
                key={o.n}
                className={`p-8 lg:p-10 ${i < onboarding.length - 1 ? 'md:border-r border-b md:border-b-0 border-[#E4DFF7]' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <span className="h-display text-5xl text-[var(--rs-coral)]">{o.n}</span>
                  <span className="w-10 h-10 rounded-lg bg-[var(--rs-green2)]/10 text-[var(--rs-green)] grid place-items-center">
                    <o.icon className="w-4 h-4" />
                  </span>
                </div>
                <h3 className="h-display text-xl text-[var(--rs-ink)] mt-6">{o.title}</h3>
                <p className="text-sm text-[var(--rs-ink)]/70 mt-2 leading-relaxed">{o.text}</p>
                {i < onboarding.length - 1 && (
                  <ArrowDownRight className="w-5 h-5 text-[var(--rs-ink)]/20 mt-6 md:hidden" />
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/contact" className="btn-primary">Start your setup <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* ── What stays the same ───────────────────────────────────── */}
      <section className="sect bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">Every single job</div>
              <h2 className="h-display text-4xl md:text-5xl text-[var(--rs-ink)] leading-tight">
                What stays the same, every job, every time.
              </h2>
              <p className="mt-5 text-[var(--rs-ink)]/70">
                The clerk changes. The postcode changes. The service changes. Four things never do.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {trust.map((t, i) => (
                <div key={i} className="rs-card p-6 flex items-start gap-4 lift">
                  <span className="w-11 h-11 rounded-lg bg-[var(--rs-coral)]/10 text-[var(--rs-coral)] grid place-items-center shrink-0">
                    <t.icon className="w-5 h-5" />
                  </span>
                  <div className="h-display text-xl text-[var(--rs-ink)] leading-snug">{t.t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Reassurance quote ─────────────────────────────────────── */}
      <section className="bg-[var(--rs-ink)] text-[var(--rs-cream)] py-14">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <CheckCircle2 className="w-9 h-9 text-[var(--rs-coral)] mx-auto mb-5" />
          <p className="h-display text-2xl md:text-3xl leading-snug">
            "It really is this simple. We email, we attend, the report turns up. Ten years of doing it the same way for a reason."
          </p>
          <div className="mt-5 text-sm text-[var(--rs-cream)]/65">Tim Bentley, Operations Manager</div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default HowItWorks;
